import { Fragment, useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";

const LENGTH = 6;
/** Visual break after the third digit, so the PIN reads as `123 – 456`. */
const GROUP = 3;

/**
 * Segmented six-digit PIN entry for the student join flow.
 *
 * A row of single-digit boxes that auto-advance as you type, so the student
 * always sees exactly how many digits are left. Fully controlled: the parent
 * owns the string `value` (digits only, max six) and gets it back through
 * `onChange`, with `onComplete` fired the moment the sixth digit lands.
 *
 * Handlers read the latest value from `valueRef` rather than the `value` prop.
 * A keystroke advances focus synchronously, and the focus event that fires
 * during that move would otherwise see the pre-render (stale) prop and snap
 * focus backwards -- which silently dropped digits when someone typed quickly.
 * `programmatic` marks those self-inflicted focus moves so only user-initiated
 * focus is allowed to reposition the caret.
 */
export function PinInput({
  value = "",
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  showProgress = false,
  readOnly = false,
  label = "Six-digit exam PIN",
}) {
  const refs = useRef([]);
  const valueRef = useRef(value);
  const programmatic = useRef(false);

  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const digits = useMemo(() => Array.from({ length: LENGTH }, (_, i) => value[i] ?? ""), [value]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  function focusAt(index) {
    const clamped = Math.max(0, Math.min(LENGTH - 1, index));
    const el = refs.current[clamped];
    if (!el) return;
    programmatic.current = true;
    el.focus();
    el.select?.();
    programmatic.current = false;
  }

  function emit(next) {
    const cleaned = next.replace(/\D/g, "").slice(0, LENGTH);
    valueRef.current = cleaned;
    onChange?.(cleaned);
    if (cleaned.length === LENGTH) onComplete?.(cleaned);
  }

  function handleInput(index, raw) {
    if (readOnly) return;
    const char = raw.replace(/\D/g, "").slice(-1);
    if (!char) return;
    const current = valueRef.current;
    emit(current.slice(0, index) + char + current.slice(index + 1));
    focusAt(index + 1);
  }

  function handlePaste(index, e) {
    if (readOnly) return;
    const pasted = e.clipboardData?.getData("text")?.replace(/\D/g, "");
    if (!pasted) return;
    e.preventDefault();
    const current = valueRef.current;
    const next = (current.slice(0, index) + pasted).slice(0, LENGTH);
    emit(next);
    focusAt(next.length);
  }

  function handleKeyDown(index, e) {
    if (readOnly) return;
    const current = valueRef.current;
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[index]) {
        emit(current.slice(0, index));
      } else if (index > 0) {
        emit(current.slice(0, index - 1));
        focusAt(index - 1);
      }
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(index + 1);
    }
  }

  /**
   * Only reposition on a genuinely user-driven focus. When focus arrives because
   * we advanced it ourselves, leave it where we put it.
   */
  function handleFocus(index) {
    if (programmatic.current) return;
    const current = valueRef.current;
    if (index > current.length) focusAt(current.length);
  }

  return (
    <div className="space-y-4">
      <div
        role="group"
        aria-label={label}
        className="flex items-center justify-center gap-1 sm:gap-3"
      >
        {digits.map((digit, index) => (
          <Fragment key={index}>
            {index === GROUP && (
              <span
                aria-hidden="true"
                className="hidden select-none text-2xl font-black text-muted/40 sm:inline"
              >
                –
              </span>
            )}
            <input
              ref={(el) => {
                refs.current[index] = el;
              }}
              value={digit}
              onChange={(e) => handleInput(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={(e) => handlePaste(index, e)}
              onFocus={() => handleFocus(index)}
              disabled={disabled}
              readOnly={readOnly}
              tabIndex={readOnly ? -1 : undefined}
              inputMode="numeric"
              autoComplete="one-time-code"
              aria-label={`Digit ${index + 1} of ${LENGTH}`}
              maxLength={1}
              className={cn(
                "size-9 rounded-xl border-2 bg-bg/70 text-center font-display text-xl font-bold sm:size-12 sm:text-2xl xl:size-14 xl:text-3xl",
                "shadow-soft outline-none transition-all",
                "focus:border-accent focus:ring-4 focus:ring-accent/20",
                "motion-reduce:transition-none",
                readOnly && "cursor-default focus:ring-0",
                digit ? "border-accent/60 text-text" : "border-border text-muted",
                disabled && "opacity-50",
              )}
            />
          </Fragment>
        ))}
      </div>

      {showProgress && (
        <div className="mx-auto flex w-full max-w-xs items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-2">
            <div
              className="h-full rounded-full bg-gradient-primary transition-all duration-300 ease-out motion-reduce:transition-none"
              style={{ width: `${(value.length / LENGTH) * 100}%` }}
            />
          </div>
          <span className="w-8 shrink-0 text-right text-xs font-semibold tabular-nums text-muted">
            {value.length}/{LENGTH}
          </span>
        </div>
      )}
    </div>
  );
}
